import React from "react";
import { CELL_TINT, dayHead, fmtHours } from "./util";

/* One row per crew member, 14 day columns, paid hours in each cell. The whole
 * fortnight has to be readable at a glance before the operator approves it,
 * so the flag colour lives on the cell itself rather than in a separate list. */
export default function Grid({ rows, days, onPickCell }) {
  if (!rows.length) {
    return (
      <p className="text-xs text-slate-500 py-6 text-center bg-slate-900/10 rounded-xl border border-dashed border-slate-800">
        No crew on the roster yet. Add drivers and offsiders in Settings.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-800/80">
      <table className="w-full text-xs border-collapse">
        <thead>
          <tr className="bg-slate-900/60">
            <th className="sticky left-0 z-10 bg-slate-900 text-left font-bold text-slate-300 px-3 py-2 min-w-[150px]">Crew</th>
            {days.map((d) => {
              const h = dayHead(d);
              return (
                <th key={d} className={`px-1 py-2 font-semibold text-center min-w-[52px] ${h.weekend ? "text-slate-500" : "text-slate-300"}`}>
                  <div className="leading-tight">{h.dow}</div>
                  <div className="text-[9px] font-normal text-slate-500">{h.dm}</div>
                </th>
              );
            })}
            <th className="px-3 py-2 font-bold text-slate-300 text-right min-w-[70px]">Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-t border-slate-800/60 hover:bg-slate-900/30">
              <td className="sticky left-0 z-10 bg-slate-950 px-3 py-2">
                <div className="font-bold text-slate-100 truncate">{row.name}</div>
                <div className={`text-[9px] font-bold uppercase tracking-widest ${row.role === "driver" ? "text-blue-400" : "text-teal-400"}`}>
                  {row.role || "crew"}
                </div>
              </td>
              {days.map((d) => {
                const cell = row.cells[d];
                const tint = cell ? CELL_TINT[cell.tier || (cell.manual ? "manual" : "")] : null;
                // Every cell opens - keying a day in and correcting one are
                // the same gesture for the operator.
                return (
                  <td key={d} className="p-0.5 text-center">
                    <button
                      onClick={() => onPickCell(row, d)}
                      title={cell ? cell.tooltip : `Add hours for ${row.name} on ${d}`}
                      className={`w-full rounded-md border px-1 py-1.5 font-mono transition-all ${
                        tint || (cell
                          ? "bg-slate-900/60 border-slate-800 text-slate-200"
                          : "bg-transparent border-transparent text-slate-700 hover:border-slate-700")
                      } hover:brightness-125`}
                    >
                      {cell ? fmtHours(cell.hours) : "·"}
                    </button>
                  </td>
                );
              })}
              <td className="px-3 py-2 text-right font-mono font-bold text-slate-100">{fmtHours(row.total)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t border-slate-800 bg-slate-900/40">
            <td className="sticky left-0 z-10 bg-slate-900 px-3 py-2 font-bold text-slate-300">All crew</td>
            {days.map((d) => (
              <td key={d} className="px-1 py-2 text-center font-mono text-slate-400">
                {fmtHours(rows.reduce((sum, r) => sum + (r.cells[d]?.hours || 0), 0)) || "—"}
              </td>
            ))}
            <td className="px-3 py-2 text-right font-mono font-black text-white">
              {fmtHours(rows.reduce((sum, r) => sum + r.total, 0))}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
